import type { KeyboardEvent } from 'react';

/**
 * Geteilte Basis-Props fuer Zahlenfelder (NumberInput), die formularuebergreifend
 * identisch formatiert werden sollen (Tausendertrennzeichen, Dezimaltrennzeichen,
 * Schrittweite etc.). Feld-spezifische Anzeige-Props (Einheiten-Anhang, Obergrenze)
 * gehoeren NICHT hierher, sondern bleiben am jeweiligen Feld.
 *
 * Bewusst KEIN `min: 0`: ein negativer Tippfehler soll nicht still auf 0 geklemmt
 * werden (Nutzer bemerkt die Korrektur sonst nicht), sondern stehen bleiben und
 * ueber die Backend-Validierung (Assert\PositiveOrZero) sichtbar rot markiert werden.
 */

/**
 * `allowedDecimalSeparators: [',']` -- Punkt gilt AUSSCHLIESSLICH als Tausendertrennzeichen.
 *
 * Mantines NumberInput bringt einen eigenen Paste-Helfer mit
 * (`@mantine/core/.../NumberInput.cjs`, `handlePaste`): er nimmt
 * `allowedDecimalSeparators` ohne das Dezimaltrennzeichen -- voreingestellt also `['.']` --
 * und ersetzt beim Einfuegen JEDES dieser Zeichen durch das Dezimaltrennzeichen. Aus dem
 * eingefuegten `15.016,51` wird damit `15,016,51`, was `decimalScale: 2` zu `15,01` kuerzt:
 * ein um Faktor 1000 falscher Wert, lautlos. Genau dieser Weg ist seit Phase 24 (D-20) ein
 * vorgesehener Arbeitsablauf -- der angezeigte Vorjahreswert ist bewusst markierbar, damit
 * er uebernommen werden kann.
 *
 * Mit `[',']` faellt die Ersetzung weg, der Punkt wird als Tausendertrennzeichen verworfen
 * und `15.016,51` kommt korrekt an. Preis: der Punkt verliert seine Doppelrolle. Damit ein
 * getippter Punkt trotzdem ein Dezimaltrennzeichen bleibt (Zehnerblock auf US-Layouts),
 * bildet `handleDecimalKey` ihn auf das Komma ab -- siehe dort.
 */
const DECIMAL_SEPARATOR_PROPS = {
  thousandSeparator: '.',
  decimalSeparator: ',',
  // Bewusst nicht `as const`: Mantines Prop-Typ ist `string[]` (veraenderbar), ein
  // `readonly [',']` aus dem umschliessenden `as const` waere nicht zuweisbar.
  allowedDecimalSeparators: [','] as string[],
} as const;

/**
 * Bildet einen getippten Punkt auf das Komma ab.
 *
 * Ohne diese Abbildung waere der Punkt nach `DECIMAL_SEPARATOR_PROPS` reines
 * Tausendertrennzeichen: getipptes `15.5` ergaebe `155` -- derselbe stille Falschwert,
 * den die Einstellung beim Einfuegen gerade beseitigt, nur ueber die Tastatur.
 *
 * Mantine ruft ein durchgereichtes `onKeyDown` VOR seiner eigenen Tastenlogik auf
 * (`handleKeyDown`), das `preventDefault` hier greift also. Eingefuegt wird ueber
 * `setRangeText`, weil das Cursorposition und Auswahl korrekt fortschreibt; das
 * anschliessende `input`-Ereignis ist noetig, damit react-number-format den neuen Wert
 * uebernimmt (React haengt seinen Handler am `input`-Ereignis, nicht an `setRangeText`).
 */
export function handleDecimalKey(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key !== '.') return;
  event.preventDefault();
  const input = event.currentTarget;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  input.setRangeText(',', start, end, 'end');
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Ganzzahlige Felder (z.B. Mitarbeiteranzahl): keine Nachkommastellen. */
export const INTEGER_INPUT_PROPS = {
  step: 1,
  ...DECIMAL_SEPARATOR_PROPS,
  onKeyDown: handleDecimalKey,
  hideControls: true,
  decimalScale: 0,
  allowDecimal: false,
} as const;

/** Dezimalfelder (z.B. Mengenangaben): zwei Nachkommastellen. */
export const DECIMAL_INPUT_PROPS = {
  decimalScale: 2,
  step: 0.01,
  ...DECIMAL_SEPARATOR_PROPS,
  onKeyDown: handleDecimalKey,
  hideControls: true,
} as const;

/**
 * Brennstoff-Mengen/Heizwerte: 4 Nachkommastellen statt 2, kein decimalScale-Cap
 * (Werte koennen praezisere Nachkommastellen als 2 benoetigen). `emissionFactor`
 * bekommt kein eigenes Preset, sondern einen Feld-Override direkt an der Zelle:
 * `{...FUEL_INPUT_PROPS, decimalScale: 6}`.
 */
export const FUEL_INPUT_PROPS = {
  step: 0.0001,
  ...DECIMAL_SEPARATOR_PROPS,
  onKeyDown: handleDecimalKey,
  hideControls: true,
} as const;

/**
 * Drehofen-Emissionsfelder (34 Messwerte je Messung): 7 Nachkommastellen (Punkt 8 vom
 * 14.09.2026, `35.1-REVIEW.md`), analog zu `FUEL_INPUT_PROPS`. Bewusst KEIN `min: 0`
 * (Pitfall 4, D-04/T-19-11): das heutige lokale `NUMBER_INPUT_PROPS` in `KilnForm.tsx`
 * traegt noch `min: 0` -- diese Alt-Konvention (abgeloest seit Phase 16-03/17-01) wird
 * hier NICHT uebernommen. Ein clientseitiges Klemmen auf 0 wuerde einen negativen
 * Tippfehler nie zum Backend durchlassen und damit die serverseitige
 * `Assert\PositiveOrZero`-Kontrolle (Plan 19-03) fuer den Melder unsichtbar machen.
 *
 * `decimalScale: 8` (Entscheidung 21.09.2026, vorher 7): `UMt_Anlage 1_Statistikportal_8`
 * fuehrt PCB WHO-TEQ mit 0,00000058 -- acht Stellen, an denen die Uebernahme bis dahin
 * scheiterte. Weitere belegte Werte: Thallium 0,0000094 (`2026-03-split-vb-db`), PCB WHO-TEQ
 * 0,0000012 (`2023-07-no-unit-column`). Die Datenbankspalten tragen `DECIMAL(14,8)`,
 * `MaxDecimalPlaces` prueft dieselbe Grenze serverseitig (`EmissionValuePrecision`) -- dieses
 * Preset verhindert nur das Eintippen einer neunten Stelle, ersetzt die Backend-Pruefung nicht.
 *
 * `oxygenReference` bekommt weiterhin kein eigenes Preset, sondern einen Feld-Override
 * analog zu `emissionFactor`: `{...EMISSION_INPUT_PROPS, decimalScale: 2}`.
 */
export const EMISSION_INPUT_PROPS = {
  decimalScale: 8,
  step: 0.00000001,
  ...DECIMAL_SEPARATOR_PROPS,
  onKeyDown: handleDecimalKey,
  hideControls: true,
} as const;

/**
 * Maximale Breite eines Eingabefeldes in einer gestapelten Feldliste (Sketch 003, Variante D).
 *
 * Die Marktformulare A/B und C listen ihre Felder untereinander, ein Feld je Zeile. Ohne Deckel
 * liefe jedes Feld ueber die volle Inhaltsbreite — bei einer zweistelligen Tonnenzahl sind das
 * ueber 1000 px fuer sechs Zeichen, und die Zuordnung von Label zu Feld zerfaellt. Der Sketch
 * zeigt die Felder auf etwa halber Breite; 560 px trifft das bei den ueblichen 1200-1400 px
 * Inhaltsbreite und bleibt auf schmalen Fenstern wirkungslos, weil `maw` nur nach oben deckelt.
 */
export const FIELD_LIST_MAX_WIDTH = 560;
